import type {
  AdminProfileCardSetWithSlots,
  WorkspaceProfileCardSetSetting,
  WorkspaceProfileCardSetWithSlots,
} from '../types/profileCard';
import { deviceCacheStorage, deviceCacheSetItem } from './deviceCacheStorage';

const CACHE_VERSION = 'v1';
const ADMIN_KEY = `cp.profileCardCatalog.admin.${CACHE_VERSION}`;
const SETTINGS_PREFIX = `cp.profileCardCatalog.settings.${CACHE_VERSION}`;
const WORKSPACE_SETS_PREFIX = `cp.profileCardCatalog.workspaceSets.${CACHE_VERSION}`;

type AdminCachePayload = {
  version: string;
  sets: AdminProfileCardSetWithSlots[];
  savedAt: number;
};

type SettingsCachePayload = {
  workspaceId: string;
  version: string;
  settings: WorkspaceProfileCardSetSetting[];
  savedAt: number;
};

type WorkspaceSetsCachePayload = {
  workspaceId: string;
  version: string;
  sets: WorkspaceProfileCardSetWithSlots[];
  savedAt: number;
};

function settingsKey(workspaceId: string): string {
  return `${SETTINGS_PREFIX}.${workspaceId.trim()}`;
}

function workspaceSetsKey(workspaceId: string): string {
  return `${WORKSPACE_SETS_PREFIX}.${workspaceId.trim()}`;
}

/** head/본문 공통: 신선도에 필요한 메타만으로 버전 문자열 생성 */
export function buildProfileCardRowVersion(
  rows: Array<{ id: string; updatedAt?: string; updated_at?: string; extra?: string | number | null }>
): string {
  if (rows.length === 0) return 'empty';
  return rows
    .map((r) => {
      const id = String(r.id ?? '').trim();
      const updated = String(r.updatedAt ?? r.updated_at ?? '').trim();
      const extra = r.extra == null || r.extra === '' ? '' : String(r.extra);
      return `${id}:${updated}:${extra}`;
    })
    .filter((s) => s.startsWith(':') === false && s.length > 2)
    .sort()
    .join('|');
}

export function buildAdminProfileCardCatalogVersion(sets: AdminProfileCardSetWithSlots[]): string {
  const setPart = buildProfileCardRowVersion(
    sets.map((s) => ({ id: s.id, updatedAt: s.updatedAt, extra: s.sortOrder }))
  );
  const slotPart = buildProfileCardRowVersion(
    sets.flatMap((s) =>
      s.slots.map((sl) => ({
        id: sl.id,
        updatedAt: sl.updatedAt,
        extra: `${sl.imageVersion}:${sl.rarity ?? ''}:${sl.storagePath ? '1' : '0'}`,
      }))
    )
  );
  return `sets:${setPart}||slots:${slotPart}`;
}

export function buildWorkspaceSettingsVersion(settings: WorkspaceProfileCardSetSetting[]): string {
  return buildProfileCardRowVersion(
    settings.map((s) => ({
      id: s.setId,
      updatedAt: s.updatedAt,
      extra: `${s.enabled ? '1' : '0'}:${s.priceCoins}`,
    }))
  );
}

export function buildWorkspaceSetsCatalogVersion(sets: WorkspaceProfileCardSetWithSlots[]): string {
  const setPart = buildProfileCardRowVersion(
    sets.map((s) => ({
      id: s.id,
      updatedAt: s.updatedAt,
      extra: `${s.enabled ? '1' : '0'}:${s.priceCoins}:${s.sortOrder}`,
    }))
  );
  const slotPart = buildProfileCardRowVersion(
    sets.flatMap((s) =>
      s.slots.map((sl) => ({
        id: sl.id,
        updatedAt: sl.updatedAt,
        extra: `${sl.imageVersion}:${sl.rarity ?? ''}:${sl.storagePath ? '1' : '0'}`,
      }))
    )
  );
  return `sets:${setPart}||slots:${slotPart}`;
}

export function loadAdminProfileCardSetsCache(): AdminCachePayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(ADMIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminCachePayload;
    if (!parsed || typeof parsed.version !== 'string' || !Array.isArray(parsed.sets)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveAdminProfileCardSetsCache(sets: AdminProfileCardSetWithSlots[], version: string): void {
  if (typeof window === 'undefined' || !version) return;
  try {
    const payload: AdminCachePayload = { version, sets, savedAt: Date.now() };
    deviceCacheSetItem(ADMIN_KEY, JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function loadWorkspaceProfileCardSettingsCache(
  workspaceId: string
): SettingsCachePayload | null {
  const ws = workspaceId.trim();
  if (!ws || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(settingsKey(ws));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SettingsCachePayload;
    if (
      !parsed ||
      parsed.workspaceId !== ws ||
      typeof parsed.version !== 'string' ||
      !Array.isArray(parsed.settings)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveWorkspaceProfileCardSettingsCache(
  workspaceId: string,
  settings: WorkspaceProfileCardSetSetting[],
  version: string
): void {
  const ws = workspaceId.trim();
  if (!ws || !version || typeof window === 'undefined') return;
  try {
    const payload: SettingsCachePayload = {
      workspaceId: ws,
      version,
      settings,
      savedAt: Date.now(),
    };
    deviceCacheSetItem(settingsKey(ws), JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function loadWorkspaceProfileCardSetsCache(
  workspaceId: string
): WorkspaceSetsCachePayload | null {
  const ws = workspaceId.trim();
  if (!ws || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(workspaceSetsKey(ws));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorkspaceSetsCachePayload;
    if (
      !parsed ||
      parsed.workspaceId !== ws ||
      typeof parsed.version !== 'string' ||
      !Array.isArray(parsed.sets)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveWorkspaceProfileCardSetsCache(
  workspaceId: string,
  sets: WorkspaceProfileCardSetWithSlots[],
  version: string
): void {
  const ws = workspaceId.trim();
  if (!ws || !version || typeof window === 'undefined') return;
  try {
    const payload: WorkspaceSetsCachePayload = {
      workspaceId: ws,
      version,
      sets,
      savedAt: Date.now(),
    };
    deviceCacheSetItem(workspaceSetsKey(ws), JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function invalidateAdminProfileCardCatalogCache(): void {
  if (typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(ADMIN_KEY);
  } catch {
    /* ignore */
  }
}

export function invalidateWorkspaceProfileCardSettingsCache(workspaceId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (workspaceId?.trim()) {
      deviceCacheStorage.removeItem(settingsKey(workspaceId.trim()));
      return;
    }
    const prefix = `${SETTINGS_PREFIX}.`;
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => deviceCacheStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function invalidateWorkspaceProfileCardSetsCache(workspaceId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (workspaceId?.trim()) {
      deviceCacheStorage.removeItem(workspaceSetsKey(workspaceId.trim()));
      return;
    }
    const prefix = `${WORKSPACE_SETS_PREFIX}.`;
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => deviceCacheStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function invalidateWorkspaceProfileCardCatalogCache(workspaceId?: string): void {
  invalidateWorkspaceProfileCardSettingsCache(workspaceId);
  invalidateWorkspaceProfileCardSetsCache(workspaceId);
}

/** 로그아웃·유저 전환 시 프로필 카드 카탈로그 LS 비우기 */
export function clearProfileCardCatalogCaches(): void {
  invalidateAdminProfileCardCatalogCache();
  invalidateWorkspaceProfileCardCatalogCache();
}
